import React, { useEffect, useRef, useState } from 'react';
import { Renderer, Stave } from 'vexflow';
import type { MusicalToken } from '../../types/music';
import { tokenToSymbol } from '../tokens';
import { ensureSalaVexFonts, resetSalaVexMetricsForPrint } from './vexFonts';
import { mapPatternToVexSpecs } from './vexFlowMapper';
import { createNoteFromSpec, styleNoteDots } from './vexNoteRender';
import { fitHostSvgToContent } from './vexSvgFit';
import { VEX_PRINT } from './vexPrintTheme';

interface VexRhythmGlyphProps {
  token: MusicalToken;
  size?: number;
  color?: string;
  className?: string;
  printMode?: boolean;
  dotted?: boolean;
}

/** Glifo VexFlow aislado para un token rítmico — fallback Unicode si falla. */
export function VexRhythmGlyph({
  token,
  size = 40,
  color = VEX_PRINT.noteColor,
  className = '',
  printMode = false,
  dotted = false,
}: VexRhythmGlyphProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (failed) return;
    const host = hostRef.current;
    if (!host) return;

    let cancelled = false;

    (async () => {
      try {
        if (printMode) resetSalaVexMetricsForPrint();
        await ensureSalaVexFonts();
        if (cancelled) return;

        host.innerHTML = '';
        const canvasW = Math.max(size + 8, 52);
        const canvasH = Math.round(size * 1.4);
        const staveY = Math.round(canvasH * 0.38);

        const renderer = new Renderer(host, Renderer.Backends.SVG);
        renderer.resize(canvasW, canvasH);
        const ctx = renderer.getContext();
        const stroke = printMode ? VEX_PRINT.noteColor : color;

        const stave = new Stave(0, staveY, canvasW);
        stave.setContext(ctx);
        stave.draw();

        const { specs } = mapPatternToVexSpecs([token], dotted ? [0] : []);
        const spec = specs[0];
        if (!spec) return;

        const note = createNoteFromSpec(spec, stroke, printMode);
        note.setStave(stave);
        note.draw();
        if (spec.dotted) styleNoteDots(note, stroke, ctx);

        fitHostSvgToContent(host, 4);

        const svg = host.querySelector('svg');
        if (svg) {
          svg.classList.add('vex-glyph-svg');
          if (printMode) svg.classList.add('vex-glyph-svg--print');
          svg.querySelectorAll('text').forEach((el) => {
            el.setAttribute('fill', stroke);
          });
        }
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, size, color, failed, printMode, dotted]);

  if (failed) {
    return (
      <span className={`vex-rhythm-glyph-fallback ${className}`.trim()} aria-hidden>
        {tokenToSymbol(token)}
      </span>
    );
  }

  return (
    <div
      ref={hostRef}
      className={`vex-rhythm-glyph ${className}`.trim()}
      style={{ width: size + 8, minHeight: Math.round(size * 1.25) }}
      aria-hidden
    />
  );
}
